import React from 'react';
import { useState } from 'react';
import { Music, Star, DollarSign, Headphones, Piano, Volume2, X, Phone, Mail } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const PianoNumeriquesNeufs = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();
  const scrollRef3 = useScrollAnimation();
  const [selectedPiano, setSelectedPiano] = useState<any>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  const pianos = [
    {
      brand: "YAMAHA",
      image: "https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=800",
      features: ["Technologie CFX", "Toucher authentique", "Connectivité moderne", "Gamme complète disponible"],
      specs: {
        gamme: "CLP, CVP, P-Series",
        technologie: "Échantillonnage CFX et Bösendorfer",
        clavier: "GrandTouch et GrandTouch-S",
        sons: "Jusqu'à 600+ voix selon modèle",
        connectivite: "Bluetooth Audio/MIDI, USB",
        origine: "Japon - Leader mondial"
      }
    },
    {
      brand: "KAWAI",
      image: "/imageskawai.jpeg",
      features: ["Grand Feel III", "Touches en bois", "Son Shigeru Kawai", "Partenaire historique"],
      specs: {
        gamme: "CA, CN, ES, MP Series",
        technologie: "Harmonic Imaging et SK-EX",
        clavier: "Grand Feel I, II, III",
        sons: "Sons Shigeru Kawai premium",
        connectivite: "Bluetooth MIDI, USB",
        origine: "Japon - Partenaire depuis des décennies"
      }
    },
    {
      brand: "ADAGIO",
      image: "/adagio.jpg",
      features: ["Tradition allemande", "Qualité premium", "Design élégant", "Savoir-faire artisanal"],
      specs: {
        gamme: "C Series, K Series",
        technologie: "Échantillonnage haute définition",
        clavier: "Toucher lesté premium",
        sons: "Sons de concert authentiques",
        connectivite: "USB, MIDI, Bluetooth",
        origine: "Allemagne - Tradition depuis 1885"
      }
    }
  ];

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div 
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-light heading-modern mb-6">
              Pianos <span className="heading-accent">Numériques Neufs</span>
            </h1>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-white/70 font-light max-w-4xl mx-auto leading-relaxed">
              Découvrez notre sélection de pianos numériques neufs alliant technologie moderne 
              et authenticité du toucher. L'excellence acoustique à portée de main.
            </p>
          </div>

          {/* Avantages */}
          <div 
            ref={scrollRef2 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal grid md:grid-cols-4 gap-6 mb-20"
          >
            {[
              { icon: DollarSign, title: "Le Prix", desc: "Moins coûteux" },
              { icon: Volume2, title: "Volume Réglable", desc: "Volume sonore réglable" },
              { icon: Headphones, title: "Jeu au Casque", desc: "Possibilité de jouer avec un casque" },
              { icon: Piano, title: "Qualité Authentique", desc: "Un très bon piano numérique se rapproche d'un piano acoustique" }
            ].map((feature, index) => (
              <div key={index} className="glass-card interactive-card rounded-2xl p-6 text-center light-effect">
                <feature.icon className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60 font-light text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collection */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef3 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <h2 className="text-3xl font-light text-center text-white mb-16">
              Notre <span className="heading-accent">Sélection Premium</span>
            </h2>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {pianos.map((piano, index) => (
                <div key={index} className="glass-card interactive-card rounded-2xl overflow-hidden light-effect">
                  <div className="relative h-64">
                    <img
                      src={piano.image}
                      alt={piano.brand}
                      className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-110"
                      style={{ objectPosition: 'center 30%' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  </div>
                  
                  <div className="p-6">
                    <div className="mb-4">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-semibold text-white">{piano.brand}</h3>
                        <span className="text-sm font-light text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full border border-yellow-400/30">
                          Prix sur devis
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-6">
                      {piano.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-white/70">
                          <Star className="w-3 h-3 text-yellow-400 mr-2 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Autres marques disponibles */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="glass-card rounded-3xl p-8 max-w-4xl mx-auto text-center">
            <p className="text-white/70 font-light text-lg leading-relaxed">
              <span className="text-yellow-400 font-semibold">D'autres marques prestigieuses</span> sont également disponibles en magasin. 
              N'hésitez pas à nous consulter pour découvrir l'ensemble de notre sélection et trouver le piano qui vous correspond parfaitement.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="glass-card rounded-3xl p-12 max-w-4xl mx-auto text-center">
            <Music className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
            <h3 className="text-3xl font-light text-white mb-6">
              Trouvez Votre <span className="heading-accent">Piano Idéal</span>
            </h3>
            <p className="text-white/70 font-light text-lg mb-8 leading-relaxed">
              Venez essayer nos pianos numériques dans notre showroom. 
              Nos experts vous accompagnent dans votre choix.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setShowAppointmentModal(true)}
                className="btn-premium px-8 py-4 rounded-full font-semibold tracking-wide"
              >
                Prendre Rendez-vous
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modal des détails */}
      {selectedPiano && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-semibold text-white">{selectedPiano.brand}</h3>
                <button 
                  onClick={() => setSelectedPiano(null)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <img 
                src={selectedPiano.image} 
                alt={selectedPiano.brand}
                className="w-full h-48 object-cover rounded-2xl mb-6"
              />
              
              <h4 className="text-lg font-semibold text-white mb-4">Caractéristiques Techniques</h4>
              
              <div className="space-y-4">
                {Object.entries(selectedPiano.specs).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-start py-2 border-b border-white/10">
                    <span className="text-white/70 font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="text-white text-right max-w-xs">{value}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 flex gap-4">
                <button 
                  onClick={() => {
                    setSelectedPiano(null);
                    scrollToContact();
                  }}
                  className="btn-premium px-6 py-3 rounded-lg font-medium flex-1"
                >
                  Contactez-nous
                </button>
                <button 
                  onClick={() => setSelectedPiano(null)}
                  className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-6 py-3 rounded-lg font-light transition-all duration-300 flex-1"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de prise de rendez-vous */}
      {showAppointmentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card rounded-3xl max-w-md w-full">
            <div className="p-8 text-center">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-semibold text-white">Prendre Rendez-vous</h3>
                <button 
                  onClick={() => setShowAppointmentModal(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="glass-card rounded-2xl p-6">
                  <Phone className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-white mb-2">Appelez-nous</h4>
                  <div className="space-y-2 text-white/70">
                    <p>Magasin : <a href="tel:+33389542003" className="text-yellow-400 hover:text-yellow-300 transition-colors">03 89 54 20 03</a></p>
                    <p>Mobile : <a href="tel:+33607971604" className="text-yellow-400 hover:text-yellow-300 transition-colors">06 07 97 16 04</a></p>
                  </div>
                </div>
                
                <div className="glass-card rounded-2xl p-6">
                  <Mail className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-white mb-2">Écrivez-nous</h4>
                  <p className="text-white/70">
                    <a href="mailto:musique.buchta@gmail.com" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                      musique.buchta@gmail.com
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="mt-8">
                <button 
                  onClick={() => setShowAppointmentModal(false)}
                  className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-8 py-3 rounded-full font-light transition-all duration-300 w-full"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PianoNumeriquesNeufs;